import * as React from 'react'

import { CompassIcon } from 'lucide-react'

import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { formatDuration } from './format'
import { Session } from './types'

interface ISessionRowProps {
  session: Session
  projectDateStore?: ProjectDateStore
  isHovered: boolean
  onHover: (id: string | null) => void
  onOpen: (session: Session) => void
}

const SessionRow: React.FC<ISessionRowProps> = React.memo(
  ({ session, projectDateStore, isHovered, onHover, onOpen }) => {
    const id = String(session.id)
    const startedAt = Number(session.startedAt || 0)
    const endedAt = Number(session.endedAt || 0)
    const isOpenSession = !!startedAt && !endedAt
    const isClickable = !!startedAt && !!endedAt
    const movingMs = Number(session.movingDurationMs || 0)
    const idleMs = Number(session.idleDurationMs || 0)

    const timeRange = projectDateStore
      ? `${projectDateStore.getTimeToDisplay(startedAt)} – ${
          endedAt
            ? projectDateStore.getTimeToDisplay(endedAt)
            : Localization.translator.activitySessionOpen
        }`
      : `${startedAt} – ${endedAt || ''}`

    const handleClick = React.useCallback(() => {
      if (isClickable) onOpen(session)
    }, [isClickable, onOpen, session])
    const handleEnter = React.useCallback(() => onHover(id), [onHover, id])
    const handleLeave = React.useCallback(() => onHover(null), [onHover])

    return (
      <li className="tw:m-0">
        <button
          type="button"
          aria-disabled={!isClickable || undefined}
          onClick={handleClick}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onFocus={handleEnter}
          onBlur={handleLeave}
          className={`tw:group tw:flex tw:w-full tw:flex-col tw:gap-0.5 tw:rounded-md tw:border-0 tw:bg-transparent tw:px-2 tw:py-2 tw:text-left tw:transition-colors tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring ${
            isClickable ? 'tw:cursor-pointer' : 'tw:cursor-default'
          } ${
            isHovered
              ? 'tw:bg-primary/8 tw:ring-1 tw:ring-inset tw:ring-primary/20'
              : 'tw:hover:bg-muted/50'
          }`}
        >
          <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-3">
            <span className="tw:flex tw:items-center tw:gap-2 tw:text-title-sm tw:text-foreground tw:tabular-nums">
              {timeRange}
              {isOpenSession && (
                <span className="tw:relative tw:flex tw:size-1.5">
                  <span className="tw:absolute tw:inline-flex tw:size-full tw:animate-ping tw:rounded-full tw:bg-primary tw:opacity-75" />
                  <span className="tw:relative tw:inline-flex tw:size-1.5 tw:rounded-full tw:bg-primary" />
                </span>
              )}
            </span>
            <span className="tw:text-body-sm tw:text-muted-foreground tw:tabular-nums">
              {formatDuration(Number(session.durationMs || 0))}
            </span>
          </div>
          <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:text-body-sm tw:text-muted-foreground tw:tabular-nums">
            <span>
              {Localization.translator.activitySessionMoving.toLowerCase()}{' '}
              <span className="tw:text-foreground">
                {formatDuration(movingMs)}
              </span>
              {idleMs > 0 && (
                <>
                  <span className="tw:px-1">·</span>
                  {Localization.translator.activitySessionIdle.toLowerCase()}{' '}
                  <span className="tw:text-foreground">
                    {formatDuration(idleMs)}
                  </span>
                </>
              )}
            </span>
            {isClickable && (
              <CompassIcon
                aria-hidden
                className="tw:size-3.5 tw:text-muted-foreground/40 tw:transition-colors tw:group-hover:text-foreground"
              />
            )}
          </div>
        </button>
      </li>
    )
  },
)
SessionRow.displayName = 'SessionRow'

interface IDayActivitySessionListProps {
  sessions: Session[]
  hoveredSessionId: string | null
  onHoverSession: (id: string | null) => void
  onOpenSession: (session: Session) => void
  projectDateStore?: ProjectDateStore
}

const DayActivitySessionList: React.FC<IDayActivitySessionListProps> =
  React.memo(
    ({
      sessions,
      hoveredSessionId,
      onHoverSession,
      onOpenSession,
      projectDateStore,
    }) => {
      return (
        <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-0.5 tw:p-0">
          {sessions.map(session => (
            <SessionRow
              key={String(session.id)}
              session={session}
              projectDateStore={projectDateStore}
              isHovered={hoveredSessionId === String(session.id)}
              onHover={onHoverSession}
              onOpen={onOpenSession}
            />
          ))}
        </ul>
      )
    },
  )
DayActivitySessionList.displayName = 'DayActivitySessionList'

export default DayActivitySessionList
